import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import { RemoteContent, Title, useAuth } from '@jupi/ui'
import { useEnrolments } from '../../api/school'
import { EnrolmentCard } from '../../components/EnrolmentCard'

// An instructor's students: everyone enrolled on a course they are
// privileged to teach (and, for a head of training, the whole
// department), grouped by course. Active training by default.
export default function MyStudentsPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { data: all = [], isLoading, error } = useEnrolments()
  const [showAll, setShowAll] = useState(false)

  const groups = useMemo(() => {
    const visible = all.filter((e) => e.studentUserId !== user?.id && (showAll || e.status === 'active'))
    const byCourse = new Map<string, typeof visible>()
    for (const e of visible) byCourse.set(e.courseName, [...(byCourse.get(e.courseName) ?? []), e])
    return [...byCourse.entries()]
  }, [all, user?.id, showAll])

  return (
    <Box>
      <Title label={t('nav.myStudents')}>
        <FormControlLabel
          control={<Switch checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />}
          label={t('school.showFinished')}
        />
      </Title>
      <RemoteContent isLoading={isLoading} error={error}>
        {groups.length === 0 && !isLoading ? <Typography color="text.secondary">{t('school.noStudents')}</Typography> : null}
        {groups.map(([courseName, enrolments]) => (
          <Box key={courseName} sx={{ mb: 3 }}>
            <Typography variant="overline" color="text.secondary">
              {courseName}
            </Typography>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              {enrolments.map((enrolment) => (
                <EnrolmentCard key={enrolment.enrolmentId} enrolment={enrolment} showStudent />
              ))}
            </Box>
          </Box>
        ))}
      </RemoteContent>
    </Box>
  )
}
